import { mqMin, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, Dialog, IndeterminateProgressCircle, Typography } from '@sunwebgroup/ui-components/uds'
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useMount } from 'react-use'
import { styled } from 'styled-components'
import { formatDate } from '@/scopes/account/common/formatters'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { useAuthProtection } from '../../common/hooks'
import userProfileUseCases from '../domain/useCases'
import { userProfileStore } from '../infrastructure/store'
import userProfileApiClient from '../infrastructure/userProfileApiClient'
import { useUserProfileStore } from '../infrastructure/useUserProfileStore'
import { DeleteAccount, DeleteAccountSuccessModal, UserProfileEditForm, UserProfileField, UserProfileLayout } from './components'

const UserProfileInfoContainer = styled.div`
  background-color: ${tok('semantics_views_background_color')};
  padding: ${tokPx('primitives_space_20')};
  width: 100%;
  display: flex;
  gap: ${tokPx('primitives_space_16')};
  flex-direction: column;
  align-items: flex-start;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    border-radius: ${tokPx('semantics_boxes_corner_radius')};
  }
`
const UserProfileInfoContainerCenterContent = styled(UserProfileInfoContainer)`
  display: flex;
  align-items: center;
  padding: ${tokPx('primitives_space_64')};
  justify-content: center;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: ${tokPx('primitives_space_8')};
  flex-direction: column;
  align-items: flex-start;
`

function UserProfileContent() {
  useAuthProtection()
  const intl = useIntl()
  const queryClient = useQueryClient()
  const userAuthenticationClient = useCommonDependency('userAuthenticationClient')
  const useCases = React.useMemo(
    () => userProfileUseCases({
      apiClient: userProfileApiClient,
      store: userProfileStore,
      invalidateUserQuery: () => {
        userAuthenticationClient.invalidateUserInfo()
        return queryClient.invalidateQueries({ queryKey: ['user'] })
      },
    }),
    [queryClient, userAuthenticationClient],
  )
  useMount(() => useCases.pageLoaded())
  const locale = useConfiguration('locale')
  const isLoading = useUserProfileStore(state => state.retrieveStatus.loading)
  const isError = useUserProfileStore(state => state.retrieveStatus.error)
  const firstName = useUserProfileStore(state => state.firstName)
  const lastName = useUserProfileStore(state => state.lastName)
  const birthDate = useUserProfileStore(state => state.birthDate)
  const isEditing = useUserProfileStore(state => state.editing)
  const setEditing = useUserProfileStore(state => state.setEditing)
  const isSaving = useUserProfileStore(state => state.saveStatus.loading)
  const isSaveSuccess = useUserProfileStore(state => state.saveStatus.success)
  const isSaveError = useUserProfileStore(state => state.saveStatus.error)
  const isAccountDeletable = useUserProfileStore(state => state.isAccountDeletable)
  const deleteStatusLoading = useUserProfileStore(state => state.deleteStatus.loading)
  const deleteStatusSuccess = useUserProfileStore(state => state.deleteStatus.success)
  const deleteStatusError = useUserProfileStore(state => state.deleteStatus.error)

  const getBanner = (): { type: 'success' | 'danger', text: string } | undefined => {
    if (deleteStatusError) {
      return { type: 'danger' as const, text: intl.formatMessage({ id: 'user_profile_delete_error' }) }
    }
    if (isSaveError) {
      return { type: 'danger' as const, text: intl.formatMessage({ id: 'save_error' }) }
    }
    if (isSaveSuccess) {
      return { type: 'success' as const, text: intl.formatMessage({ id: 'save_success' }) }
    }
    return undefined
  }
  const fullName = `${firstName} ${lastName}`.trim()
  const banner = getBanner()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setFocus,
  } = useForm({
    defaultValues: {
      firstName,
      lastName,
      birthDate,
    },
  })

  React.useEffect(() => {
    if (isEditing) {
      setFocus('firstName')
    }
  }, [isEditing, setFocus])

  function handleEditClick() {
    reset({
      firstName,
      lastName,
      birthDate,
    })
    setEditing(true)
  }

  async function onSubmit(data: { firstName: string, lastName: string, birthDate: string }) {
    await useCases.updateProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      birthDate: data.birthDate,
    })
  }

  if (isLoading || isSaving || deleteStatusLoading) {
    return (
      <UserProfileLayout>
        <UserProfileInfoContainerCenterContent>
          <IndeterminateProgressCircle />
        </UserProfileInfoContainerCenterContent>
      </UserProfileLayout>
    )
  }

  if (isError) {
    return (
      <UserProfileLayout>
        <UserProfileInfoContainerCenterContent>
          <Typography variant="body-m-regular-tight">
            <Dialog
              text={intl.formatMessage({ id: 'load_error' })}
              supportingText={intl.formatMessage({ id: 'load_error_supporting_text' })}
              type="alert"
            />
          </Typography>
        </UserProfileInfoContainerCenterContent>
      </UserProfileLayout>
    )
  }

  return (
    <UserProfileLayout banner={banner}>
      <UserProfileInfoContainer>
        <Typography forwardedAs="h2" variant="display-h-regular">{intl.formatMessage({ id: 'user_profile_details_title' })}</Typography>
        {!isEditing
          ? (
              <>
                <UserProfileField
                  icon="Person"
                  label={intl.formatMessage({ id: 'user_profile_name_label' })}
                  value={fullName}
                />
                <UserProfileField
                  icon="Cake"
                  label={intl.formatMessage({ id: 'common_birthdate_label' })}
                  value={formatDate(birthDate, locale)}
                />
                <ButtonsContainer>
                  <Button
                    variant="secondary"
                    size="XS"
                    onClick={handleEditClick}
                  >
                    {intl.formatMessage({ id: 'user_profile_edit' })}
                  </Button>
                  {isAccountDeletable && <DeleteAccount useCases={useCases} />}
                  {deleteStatusSuccess && (
                    <DeleteAccountSuccessModal />
                  )}
                </ButtonsContainer>
              </>
            )
          : (
              <UserProfileEditForm
                errors={errors}
                register={register}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                isSaving={isSaving}
                intl={intl}
              />
            )}
      </UserProfileInfoContainer>
    </UserProfileLayout>
  )
}

const queryClient = new QueryClient()

export function UserProfile() {
  return (
    <QueryClientProvider client={queryClient}>
      <MetaTitle titleTranslationTag="user_profile_meta_title" />
      <UserProfileContent />
    </QueryClientProvider>
  )
}
